import React, { useState, useEffect, useRef, useCallback } from 'react';

const Telepresence = () => {
  // Config - exactly as in viewer.html
  const API_BASE = "https://qure.science/telepresence-api";
  const APP_ID = "b484c07fc06643c08ad94b428d6120d4";
  const DEBUG_UI = new URLSearchParams(window.location.search).get("debug") === "1";

  // State
  const [code, setCode] = useState('');
  const [joined, setJoined] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [status, setStatus] = useState({ text: 'Viewer ready', kind: 'warn' });
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [meta, setMeta] = useState({ channel: 'Channel: -', uid: 'UID: -', video: 'Video: -', talkUid: 'Talk UID: -' });
  const [logBuffer, setLogBuffer] = useState([]);

  // Refs
  const codeInputRef = useRef(null);
  const videoHostRef = useRef(null);
  const videoStageRef = useRef(null);
  const placeholderRef = useRef(null);
  const zoomRangeRef = useRef(null);
  const zoomLabelRef = useRef(null);
  const logRef = useRef(null);
  const debugWrapRef = useRef(null);
  const micIconRef = useRef(null);

  // Agora refs
  const clientRef = useRef(null);
  const talkClientRef = useRef(null);
  const talkTrackRef = useRef(null);
  const hostUidSeenRef = useRef(1);
  const viewerUidRef = useRef(null);
  const talkUidRef = useRef(null);
  const channelNameRef = useRef(null);
  const viewerTokenRef = useRef(null);
  const viewerTokenTtlSecRef = useRef(600);
  const talkTokenRef = useRef(null);
  const talkTokenTtlSecRef = useRef(60);
  const renewTimerRef = useRef(null);
  const talkKeepAliveTimerRef = useRef(null);
  const _hostVideoElRef = useRef(null);

  // Pointer/touch refs
  const pointersRef = useRef(new Map());
  const lastPanPointRef = useRef(null);
  const pinchStartDistRef = useRef(0);
  const pinchStartZoomRef = useRef(1);

  // Load Agora SDK
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://download.agora.io/sdk/release/AgoraRTC_N.js';
    script.async = true;
    document.body.appendChild(script);

    if (debugWrapRef.current) {
      debugWrapRef.current.style.display = DEBUG_UI ? 'block' : 'none';
    }

    // Initialize
    setStatusState('Viewer ready', 'warn');
    setMicUI(false);
    setZoomState(1, true);
    log('Viewer ready.');

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      safeLeave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logBuffer]);

  const ts = () => {
    const d = new Date();
    return d.toTimeString().slice(0, 8);
  };

  const log = useCallback((msg) => {
    const line = `[${ts()}] ${msg}`;
    setLogBuffer(prev => {
      const newBuffer = [...prev, line];
      return newBuffer.slice(-600);
    });
    console.log(line);
    
    if (DEBUG_UI && logRef.current) {
      logRef.current.textContent += line + '\n';
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, []);

  const setStatusState = useCallback((text, kind) => {
    setStatus({ text, kind: kind || 'warn' });
  }, []);

  const setMicUI = useCallback((on) => {
    setMicOn(on);
    if (micIconRef.current) {
      micIconRef.current.innerHTML = on
        ? `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
             <path d="M12 14a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v4a3 3 0 0 0 3 3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
             <path d="M5 11v1a7 7 0 0 0 14 0v-1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
             <path d="M12 19v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
             <path d="M8 21h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
           </svg>`
        : `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
             <path d="M9 10v2a3 3 0 0 0 6 0V7a3 3 0 0 0-5.2-2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
             <path d="M5 11v1a7 7 0 0 0 12 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
             <path d="M12 19v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
             <path d="M8 21h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
             <path d="M4 4l16 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
           </svg>`;
    }
  }, []);

  // API helper
  const apiPost = useCallback(async (path, body, timeoutMs = 12000) => {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
        signal: controller.signal
      });
      const text = await res.text();
      let json = null;
      try { json = JSON.parse(text); } catch { /* ignore */ }
      if (!res.ok) {
        const errMsg = json?.error || text || `${res.status}`;
        throw new Error(`${res.status} ${errMsg}`);
      }
      return json ?? {};
    } finally {
      clearTimeout(t);
    }
  }, []);

  // Zoom/Pan
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const applyTransform = useCallback(() => {
    if (videoHostRef.current) {
      videoHostRef.current.style.setProperty('--zoom', String(zoom));
      videoHostRef.current.style.setProperty('--panX', `${panX}px`);
      videoHostRef.current.style.setProperty('--panY', `${panY}px`);
    }
    if (zoomLabelRef.current) {
      zoomLabelRef.current.textContent = `${Math.round(zoom * 100)}%`;
    }
    if (videoStageRef.current) {
      videoStageRef.current.classList.toggle('zoomed', zoom > 1.01);
    }
  }, [zoom, panX, panY]);

  const setZoomState = useCallback((z, resetPan = false) => {
    const newZoom = clamp(z, 1, 3);
    let newPanX = panX;
    let newPanY = panY;
    
    if (resetPan) {
      newPanX = 0;
      newPanY = 0;
    }

    // Clamp pan based on stage size
    const w = videoStageRef.current?.clientWidth || 0;
    const h = videoStageRef.current?.clientHeight || 0;
    const maxX = (newZoom - 1) * w * 0.5;
    const maxY = (newZoom - 1) * h * 0.5;
    newPanX = clamp(newPanX, -maxX, maxX);
    newPanY = clamp(newPanY, -maxY, maxY);

    setZoom(newZoom);
    setPanX(newPanX);
    setPanY(newPanY);
    
    if (zoomRangeRef.current) {
      zoomRangeRef.current.value = String(newZoom);
    }
  }, [panX, panY]);

  const stepZoom = useCallback((delta) => {
    setZoomState(zoom + delta, false);
  }, [zoom, setZoomState]);

  // Video aspect sync
  const syncHostVideoAspect = useCallback(() => {
    const stage = videoStageRef.current;
    const vid = videoHostRef.current?.querySelector('video');
    if (!stage || !vid) return;

    const w = vid.videoWidth;
    const h = vid.videoHeight;
    if (!(w > 0 && h > 0)) return;

    const key = `${w}x${h}`;
    if (syncHostVideoAspect._lastKey === key) return;
    syncHostVideoAspect._lastKey = key;

    stage.style.aspectRatio = `${w} / ${h}`;

    const orient = (w >= h) ? 'landscape' : 'portrait';
    setMeta(prev => ({ ...prev, video: `Video: ${w}x${h} (${orient})` }));
    log(`Host video resolution detected: ${key}`);
  }, [log]);

  const hookHostVideoElement = useCallback(() => {
    const vid = videoHostRef.current?.querySelector('video');
    if (!vid || vid === _hostVideoElRef.current) return;
    _hostVideoElRef.current = vid;

    vid.addEventListener('loadedmetadata', () => { syncHostVideoAspect(); applyTransform(); });
    vid.addEventListener('resize', syncHostVideoAspect);

    // Fallback poll
    let last = '';
    const t = setInterval(() => {
      if (!joined || !_hostVideoElRef.current) { clearInterval(t); return; }
      const w = _hostVideoElRef.current.videoWidth;
      const h = _hostVideoElRef.current.videoHeight;
      const k = `${w}x${h}`;
      if (w > 0 && h > 0 && k !== last) {
        last = k;
        syncHostVideoAspect();
      }
    }, 600);

    syncHostVideoAspect();
  }, [joined, syncHostVideoAspect, applyTransform]);

  // Agora event wiring
  const wireClientEvents = useCallback(() => {
    if (!clientRef.current) return;

    clientRef.current.on('connection-state-change', (cur, prev, reason) => {
      log(`Conn state: ${prev} -> ${cur} (${reason || ''})`);
    });

    clientRef.current.on('user-joined', (user) => {
      log(`Remote user joined: uid=${user.uid}`);
    });

    clientRef.current.on('user-left', async (user) => {
      log(`Remote user left: uid=${user.uid}`);
      if (user.uid === hostUidSeenRef.current) {
        log('Host left. Leaving session.');
        try { await handleLeave(); } catch (_) { }
      }
    });

    clientRef.current.on('user-published', async (user, mediaType) => {
      try {
        if (mediaType === 'video' && user.uid !== hostUidSeenRef.current) {
          log(`Ignoring non-host video track. uid=${user.uid}`);
          return;
        }
        if (mediaType === 'audio' && user.uid === talkUidRef.current) {
          log(`Ignoring our own audio echo (uid=${user.uid})`);
          return;
        }

        await clientRef.current.subscribe(user, mediaType);

        if (mediaType === 'video') {
          if (placeholderRef.current) placeholderRef.current.style.display = 'none';
          if (videoHostRef.current) {
            videoHostRef.current.innerHTML = '';
            user.videoTrack.play(videoHostRef.current, { fit: 'contain' });
          }

          setTimeout(() => { hookHostVideoElement(); syncHostVideoAspect(); applyTransform(); }, 60);
          setTimeout(() => { hookHostVideoElement(); syncHostVideoAspect(); }, 250);

          log(`Host published: uid=${user.uid} type=video`);
        } else if (mediaType === 'audio') {
          user.audioTrack.play();
          const who = (user.uid === hostUidSeenRef.current) ? 'Host' : 'Viewer';
          log(`${who} published: uid=${user.uid} type=audio`);
        }
      } catch (e) {
        log(`Subscribe error: ${e?.message || e}`);
      }
    });

    clientRef.current.on('user-unpublished', (user, mediaType) => {
      log(`User unpublished: uid=${user.uid} type=${mediaType}`);
      if (mediaType === 'video' && user.uid === hostUidSeenRef.current) {
        if (placeholderRef.current) placeholderRef.current.style.display = '';
        if (videoHostRef.current) videoHostRef.current.innerHTML = '';
      }
    });
  }, [log, syncHostVideoAspect, applyTransform, hookHostVideoElement]);

  // Token renew
  const startViewerRenewLoop = useCallback(() => {
    stopViewerRenewLoop();
    const intervalMs = Math.max(15, viewerTokenTtlSecRef.current - 15) * 1000;

    renewTimerRef.current = setInterval(async () => {
      if (!joined || !clientRef.current || !code || !viewerUidRef.current) return;

      try {
        const r = await apiPost('/api/session/renew', { code, uid: viewerUidRef.current, role: 'subscriber' });
        if (r?.token) {
          await clientRef.current.renewToken(r.token);
          log('Viewer token renewed.');
        }
      } catch (e) {
        const msg = (e && e.message) ? e.message : String(e);
        log(`Viewer renew failed: ${msg}`);
        if (msg.includes('404') || msg.toLowerCase().includes('session not found')) {
          log('Session ended on server. Leaving.');
          try { await handleLeave(); } catch (_) { }
        }
      }
    }, intervalMs);
  }, [joined, code, apiPost, log]);

  const stopViewerRenewLoop = useCallback(() => {
    if (renewTimerRef.current) {
      clearInterval(renewTimerRef.current);
      renewTimerRef.current = null;
    }
  }, []);

  // Talkback
  const randomTalkUid = () => {
    for (let i = 0; i < 12; i++) {
      const u = Math.floor(100000000 + Math.random() * 900000000);
      if (u !== 1 && u !== viewerUidRef.current) return u;
    }
    return Math.floor(200000000 + Math.random() * 700000000);
  };

  const startTalkKeepAlive = useCallback(() => {
    stopTalkKeepAlive();
    const intervalMs = Math.max(10, talkTokenTtlSecRef.current - 10) * 1000;

    talkKeepAliveTimerRef.current = setInterval(async () => {
      if (!micOn || !talkClientRef.current || !code || !talkUidRef.current) return;
      try {
        const t = await apiPost('/api/session/talk/start', { code, uid: talkUidRef.current });
        if (t?.token) {
          await talkClientRef.current.renewToken(t.token);
          log('Talk token renewed.');
        }
      } catch (e) {
        log(`Talk renew failed: ${e.message}`);
      }
    }, intervalMs);
  }, [micOn, code, apiPost, log]);

  const stopTalkKeepAlive = useCallback(() => {
    if (talkKeepAliveTimerRef.current) {
      clearInterval(talkKeepAliveTimerRef.current);
      talkKeepAliveTimerRef.current = null;
    }
  }, []);

  const stopTalk = useCallback(async (silent = false) => {
    stopTalkKeepAlive();

    try {
      if (talkClientRef.current && talkTrackRef.current) {
        await talkClientRef.current.unpublish([talkTrackRef.current]);
      }
    } catch { }

    try {
      if (talkTrackRef.current) {
        talkTrackRef.current.stop();
        talkTrackRef.current.close();
      }
    } catch { }

    try {
      if (talkClientRef.current) {
        await talkClientRef.current.leave();
      }
    } catch { }

    try {
      if (code && talkUidRef.current) {
        await apiPost('/api/session/talk/stop', { code, uid: talkUidRef.current });
      }
    } catch { }

    talkClientRef.current = null;
    talkTrackRef.current = null;
    talkTokenRef.current = null;
    talkUidRef.current = null;

    setMeta(prev => ({ ...prev, talkUid: 'Talk UID: -' }));
    setMicUI(false);

    if (!silent) {
      setStatusState('Mic OFF', joined ? 'good' : 'warn');
      log('Mic is OFF.');
    }
  }, [code, joined, apiPost, log, setStatusState, setMicUI]);

  const startTalk = useCallback(async () => {
    if (!joined || !channelNameRef.current) {
      setStatusState('Join first', 'bad');
      return;
    }

    setStatusState('Enabling mic…', 'warn');
    log('Requesting talk token...');

    const newTalkUid = randomTalkUid();

    let talk;
    try {
      talk = await apiPost('/api/session/talk/start', { code, uid: newTalkUid });
    } catch (e) {
      const m = (e && e.message) ? e.message : String(e);
      if (m.includes('409') || m.toLowerCase().includes('currently talking')) {
        setStatusState('Mic busy (server single-talk)', 'bad');
      } else {
        setStatusState('Mic token failed', 'bad');
      }
      log(`Talk token error: ${e.message}`);
      setMicUI(false);
      return;
    }

    talkTokenRef.current = talk.token;
    talkTokenTtlSecRef.current = Number(talk.tokenTtlSec || 60);
    talkUidRef.current = Number(talk.uid);
    setMeta(prev => ({ ...prev, talkUid: `Talk UID: ${talkUidRef.current}` }));

    talkClientRef.current = window.AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });

    try {
      await talkClientRef.current.setClientRole('host');
      await talkClientRef.current.join(APP_ID, channelNameRef.current, talkTokenRef.current, talkUidRef.current);

      log('Opening microphone...');
      talkTrackRef.current = await window.AgoraRTC.createMicrophoneAudioTrack();
      await talkClientRef.current.publish([talkTrackRef.current]);

      setMicUI(true);
      setStatusState('Mic ON', 'good');
      log('Mic is ON. Sending audio.');

      startTalkKeepAlive();
    } catch (e) {
      log(`Mic error: ${e?.message || e}`);
      setStatusState('Mic failed', 'bad');
      await stopTalk(true);
    }
  }, [joined, code, apiPost, log, setStatusState, setMicUI, startTalkKeepAlive, stopTalk]);

  // Join/Leave
  const safeLeave = useCallback(async () => {
    if (micOn) await stopTalk(true);

    stopViewerRenewLoop();

    try {
      if (clientRef.current) {
        clientRef.current.removeAllListeners();
        await clientRef.current.leave();
      }
    } catch { }

    clientRef.current = null;
    setJoined(false);

    setMicUI(false);

    if (placeholderRef.current) placeholderRef.current.style.display = '';
    if (videoHostRef.current) videoHostRef.current.innerHTML = '';

    setMeta({ channel: 'Channel: -', uid: 'UID: -', video: 'Video: -', talkUid: 'Talk UID: -' });

    channelNameRef.current = null;
    viewerUidRef.current = null;
    viewerTokenRef.current = null;
    hostUidSeenRef.current = 1;

    setZoomState(1, true);

    setStatusState('Disconnected', 'warn');
  }, [micOn, stopTalk, stopViewerRenewLoop, setZoomState, setStatusState, setMicUI]);

  const handleLeave = useCallback(async () => {
    log('Leaving…');
    await safeLeave();
  }, [log, safeLeave]);

  const handleJoin = useCallback(async () => {
    const codeValue = (code || '').trim();
    if (!/^\d{6}$/.test(codeValue)) {
      setStatusState('Enter a valid 6-digit code', 'bad');
      return;
    }

    setStatusState('Joining…', 'warn');
    log(`Joining code ${codeValue}...`);

    let data;
    try {
      data = await apiPost('/api/session/join', { code: codeValue });
    } catch (e) {
      setStatusState('Join failed', 'bad');
      log(`Join error: ${e.message}`);
      return;
    }

    channelNameRef.current = data.channel;
    viewerUidRef.current = Number(data.uid);
    hostUidSeenRef.current = Number(data.hostUid || 1);
    viewerTokenRef.current = data.token;
    viewerTokenTtlSecRef.current = Number(data.tokenTtlSec || 600);

    setMeta(prev => ({
      ...prev,
      channel: `Channel: ${channelNameRef.current}`,
      uid: `UID: ${viewerUidRef.current}`
    }));

    if (!window.AgoraRTC) {
      setStatusState('Agora SDK not loaded', 'bad');
      log('Agora SDK not loaded');
      return;
    }

    clientRef.current = window.AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });
    wireClientEvents();

    try {
      await clientRef.current.setClientRole('audience');
      await clientRef.current.join(APP_ID, channelNameRef.current, viewerTokenRef.current, viewerUidRef.current);

      setJoined(true);
      setStatusState('Connected', 'good');
      log('Joined as audience. Waiting for host publish...');
      startViewerRenewLoop();

      setZoomState(1, true);
      hookHostVideoElement();
    } catch (e) {
      setStatusState('Agora join failed', 'bad');
      log(`Agora join failed: ${e?.message || e}`);
      await safeLeave();
    }
  }, [code, apiPost, log, setStatusState, wireClientEvents, startViewerRenewLoop, setZoomState, hookHostVideoElement, safeLeave]);

  const handleMicToggle = useCallback(async () => {
    if (!joined) return;
    if (!micOn) await startTalk();
    else await stopTalk();
  }, [joined, micOn, startTalk, stopTalk]);

  // Pointer/touch handlers
  useEffect(() => {
    const stage = videoStageRef.current;
    if (!stage) return;

    const dist = (a, b) => {
      const dx = a.x - b.x, dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handlePointerDown = (e) => {
      stage.setPointerCapture(e.pointerId);
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointersRef.current.size === 1) {
        lastPanPointRef.current = { x: e.clientX, y: e.clientY };
      } else if (pointersRef.current.size === 2) {
        const pts = Array.from(pointersRef.current.values());
        pinchStartDistRef.current = dist(pts[0], pts[1]);
        pinchStartZoomRef.current = zoom;
        lastPanPointRef.current = null;
      }
    };

    const handlePointerMove = (e) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      // Pinch zoom
      if (pointersRef.current.size === 2) {
        const pts = Array.from(pointersRef.current.values());
        const d = dist(pts[0], pts[1]);
        if (pinchStartDistRef.current > 0) {
          const ratio = d / pinchStartDistRef.current;
          setZoomState(pinchStartZoomRef.current * ratio, false);
        }
        return;
      }

      // Pan only when zoomed
      if (zoom <= 1.01 || !lastPanPointRef.current) return;

      const dx = e.clientX - lastPanPointRef.current.x;
      const dy = e.clientY - lastPanPointRef.current.y;
      lastPanPointRef.current = { x: e.clientX, y: e.clientY };

      setPanX(prev => {
        const newPanX = prev + dx;
        const w = stage.clientWidth || 0;
        const maxX = (zoom - 1) * w * 0.5;
        return clamp(newPanX, -maxX, maxX);
      });
      setPanY(prev => {
        const newPanY = prev + dy;
        const h = stage.clientHeight || 0;
        const maxY = (zoom - 1) * h * 0.5;
        return clamp(newPanY, -maxY, maxY);
      });
    };

    const clearPointer = (e) => {
      pointersRef.current.delete(e.pointerId);
      if (pointersRef.current.size < 2) { pinchStartDistRef.current = 0; }
      if (pointersRef.current.size === 0) { lastPanPointRef.current = null; }
    };

    stage.addEventListener('pointerdown', handlePointerDown);
    stage.addEventListener('pointermove', handlePointerMove);
    stage.addEventListener('pointerup', clearPointer);
    stage.addEventListener('pointercancel', clearPointer);

    // Wheel zoom
    const handleWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = (e.deltaY > 0) ? -0.06 : 0.06;
      setZoomState(zoom + delta, false);
    };
    stage.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      stage.removeEventListener('pointerdown', handlePointerDown);
      stage.removeEventListener('pointermove', handlePointerMove);
      stage.removeEventListener('pointerup', clearPointer);
      stage.removeEventListener('pointercancel', clearPointer);
      stage.removeEventListener('wheel', handleWheel);
    };
  }, [zoom, setZoomState]);

  // Apply transform when zoom/pan changes
  useEffect(() => {
    applyTransform();
  }, [applyTransform]);

  const handleCopyLog = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(logBuffer.join('\n'));
      if (DEBUG_UI) log('Copied log to clipboard.');
    } catch {
      if (DEBUG_UI) log('Copy failed (clipboard permissions).');
    }
  }, [logBuffer, log]);

  return (
    <div className="telepresence-page">
      <div className="telepresence-container">
        <header className="telepresence-header">
          <div className="telepresence-brand">
            <div className="telepresence-logo" aria-hidden="true"></div>
            <div className="telepresence-brandText">
              <div className="telepresence-brandTitle">QureXR Telepresence</div>
              <div className="telepresence-brandSub">Join a 6‑digit code to watch the host. Use the mic icon to talk.</div>
            </div>
          </div>

          <div className="telepresence-controls">
            <div className="telepresence-codeGroup">
              <label className="sr-only" htmlFor="code">6-digit code</label>
              <input
                id="code"
                ref={codeInputRef}
                type="text"
                placeholder="6-digit code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setCode(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleJoin();
                }}
              />
              <button
                id="btnJoin"
                className="telepresence-btn telepresence-btn-primary"
                onClick={handleJoin}
                disabled={joined}
              >
                Join
              </button>
              <button
                id="btnLeave"
                className="telepresence-btn"
                onClick={handleLeave}
                disabled={!joined}
              >
                Leave
              </button>
            </div>

            <button
              id="btnMic"
              className={`telepresence-iconBtn ${micOn ? 'on' : ''}`}
              disabled={!joined}
              onClick={handleMicToggle}
              aria-label="Toggle microphone"
              title={micOn ? 'Microphone: ON (click to mute)' : 'Microphone: OFF (click to talk)'}
            >
              <span id="micIcon" ref={micIconRef} aria-hidden="true"></span>
            </button>

            <div className="telepresence-statusPill" aria-live="polite">
              <span className={`telepresence-dot ${status.kind}`}></span>
              <span id="status">{status.text}</span>
            </div>
          </div>
        </header>

        <main className="telepresence-main">
          <section className="telepresence-card">
            <div className="telepresence-cardHead">
              <div>
                <div className="telepresence-h">Host Video</div>
                <div className="telepresence-meta" id="metaLine">
                  <span id="metaChan">{meta.channel}</span>
                  <span id="metaUid">{meta.uid}</span>
                  <span id="metaVideo">{meta.video}</span>
                </div>
              </div>

              <div className="telepresence-zoomRow" aria-label="Zoom controls">
                <button
                  id="zoomOut"
                  className="telepresence-btn telepresence-btn-small"
                  type="button"
                  title="Zoom out"
                  onClick={() => stepZoom(-0.10)}
                >
                  −
                </button>
                <input
                  id="zoomRange"
                  ref={zoomRangeRef}
                  type="range"
                  min="1"
                  max="3"
                  step="0.01"
                  value={zoom}
                  onChange={(e) => setZoomState(Number(e.target.value), false)}
                />
                <button
                  id="zoomIn"
                  className="telepresence-btn telepresence-btn-small"
                  type="button"
                  title="Zoom in"
                  onClick={() => stepZoom(0.10)}
                >
                  +
                </button>
                <span id="zoomLabel" ref={zoomLabelRef} className="telepresence-zoomLabel">100%</span>
                <button
                  id="zoomReset"
                  className="telepresence-btn telepresence-btn-small telepresence-btn-ghost"
                  type="button"
                  title="Reset zoom & position"
                  onClick={() => setZoomState(1, true)}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="telepresence-stageWrap">
              <div className="telepresence-stage" ref={videoStageRef} id="videoStage">
                <div id="videoHost" ref={videoHostRef} className="telepresence-videoHost"></div>
                <div id="placeholder" ref={placeholderRef} className="telepresence-placeholder">Waiting for host video…</div>
              </div>
              <div className="telepresence-hint">Zoom with the slider (or pinch on mobile). When zoomed in, drag the video to pan.</div>
            </div>
          </section>

          <details id="debugWrap" ref={debugWrapRef} className="telepresence-debug">
            <summary>Diagnostics</summary>
            <div className="telepresence-debugInner">
              <div className="telepresence-debugRow">
                <span id="metaTalk">{meta.talkUid}</span>
                <button
                  id="btnCopyLog"
                  className="telepresence-btn telepresence-btn-small"
                  type="button"
                  onClick={handleCopyLog}
                >
                  Copy log
                </button>
              </div>
              <pre id="log" ref={logRef} className="telepresence-logBox">
                {logBuffer.join('\n')}
              </pre>
            </div>
          </details>
        </main>
      </div>
    </div>
  );
};

export default Telepresence;
